import type {
  Geometry as MartinezGeometry,
  Polygon as MartinezPolygon,
  Ring as MartinezRing,
} from 'martinez-polygon-clipping';
import { safeMartinezIntersection } from './martinez-polygon-clipping-ops.helpers';
import {
  IRect,
  ISize,
  IPosition,
  Point2D,
  PointLike,
  Polygon2D,
  Line2D,
} from '@shared/models/geometry.model';
import { required } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { clamp, isCloseTo } from '../math.helpers';
import { getDistance } from './coordinates.helpers';
import { OptionalReadonly } from '@shared/models/type-helpers.model';

// Geometry helpers for planar 2D shapes

const INVALID_POLYGON_INPUT = 'Invalid polygon input';

/**
 * Computes the area of a polygon
 * @param points Points of the polygon
 * @returns Area of the polygon
 */
export const computePolygonArea = (points: ReadonlyArray<Point2D>): number => {
  // A polygon has no area if it has less than 3 points
  if (!isPolygon2D(points)) return 0;

  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = required(points[i], INVALID_POLYGON_INPUT);
    const [x2, y2] = required(
      points[(i + 1) % points.length],
      INVALID_POLYGON_INPUT,
    );
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
};

/**
 * Computes the perimeter of a polygon
 * @param points Points of the polygon
 * @returns Perimeter of the polygon
 */
export const computePolygonPerimeter = (
  points: ReadonlyArray<Point2D>,
): number => {
  // A line has no perimeter if it has less than 2 points
  if (points.length < 2) return 0;
  let length = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = required(points[i], INVALID_POLYGON_INPUT);
    const p2 = required(points[(i + 1) % points.length], INVALID_POLYGON_INPUT);

    length += getDistance(p1, p2);
  }
  return length;
};

const defaultPosition: Readonly<IPosition> = Object.freeze({ x: 0, y: 0 });
const defaultSize: Readonly<ISize> = Object.freeze({ width: 0, height: 0 });
const defaultRect: Readonly<IRect> = Object.freeze({
  ...defaultPosition,
  ...defaultSize,
});

export const getRectFromDragEvent = (
  event: React.DragEvent<HTMLElement>,
): IRect => {
  const target = getDraggableParent(event.target as HTMLDivElement);
  const native = event.nativeEvent;

  if (!target) {
    return { ...defaultRect };
  }

  return {
    x: native.x,
    y: native.y,
    width: target?.clientWidth || 0,
    height: target?.clientHeight || 0,
  };
};

const getDraggableParent = (child: HTMLElement): HTMLElement | undefined => {
  let parent: HTMLElement | null = child;
  while ((parent = parent?.parentElement)) {
    if (parent?.getAttribute('draggable')) {
      return parent;
    }
  }
};

export const getRectFromElement = (element?: HTMLElement): IRect => {
  if (!element) {
    return { ...defaultRect };
  }

  return {
    x: element?.clientLeft,
    y: element?.clientTop,
    width: element?.clientWidth,
    height: element?.clientHeight,
  };
};

/**
 * Returns the relative position of a point within a rectangle.
 * @param rect The rectangle to get the relative position within.
 * @param pos The position to get the relative position of.
 * @returns The relative position of the point within the rectangle.
 * @example getRelativePositionWithinRect({ x: 0, y: 0, width: 100, height: 100 }, { x: 50, y: 50 }) // { x: 0.5, y: 0.5 }
 * @example getRelativePositionWithinRect({ x: 0, y: 0, width: 100, height: 100 }, { x: 0, y: 0 }) // { x: 0, y: 0 }
 * @example getRelativePositionWithinRect({ x: 0, y: 0, width: 100, height: 100 }, { x: 100, y: 100 }) // { x: 1, y: 1 }
 */
export const getRelativePositionWithinRect = (
  rect: IRect,
  pos: IPosition,
): IPosition => {
  const x = (pos.x - rect.x) / rect.width;
  const y = (pos.y - rect.y) / rect.height;

  return { x, y };
};

export const getIntersection = (a: IRect, b: IRect): IRect => {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const width = Math.min(a.x + a.width, b.x + b.width) - x;
  const height = Math.min(a.y + a.height, b.y + b.height) - y;

  if (width <= 0 || height <= 0) return { ...defaultRect };

  return { x, y, width, height };
};

export const mergeRectangles = (a: IRect, b: IRect): IRect => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const width = Math.max(a.x + a.width, b.x + b.width) - x;
  const height = Math.max(a.y + a.height, b.y + b.height) - y;

  return { x, y, width, height };
};

export const degreesToRadians = (degrees: number): number =>
  degrees * (Math.PI / 180);

export const isHorizontalOverlap = <T extends Pick<IRect, 'x' | 'width'>>(
  a: T,
  b: T,
): boolean => a.x < b.x + b.width && a.x + a.width > b.x;

export const isVerticalOverlap = <T extends Pick<IRect, 'y' | 'height'>>(
  a: T,
  b: T,
): boolean => a.y < b.y + b.height && a.y + a.height > b.y;

export const spaceEvenlyHorizontally = <T extends Pick<IRect, 'x' | 'width'>>(
  items: T[],
  start?: number,
  end?: number,
): T[] => {
  if (items.length < 2) {
    return items;
  }

  const sorted = [...items].sort((a, b) => a.x - b.x);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  if (first === undefined || last === undefined) {
    return items;
  }

  const startPos = start ?? first.x;
  const endPos = end ?? last.x + last.width - startPos;
  const totalWidth = sorted.reduce((acc, item) => acc + item.width, 0);
  const spacing = (endPos - startPos - totalWidth) / (sorted.length - 1);

  let prevPos = startPos - spacing;
  return sorted.map((item) => {
    const x = prevPos + spacing;
    const distributed = {
      ...item,
      x,
    };
    prevPos = x + item.width;
    return distributed;
  });
};

export const isPoint2D = (point: unknown): point is Point2D => {
  return (
    guards.is.array(point) &&
    point.length === 2 &&
    guards.is.number(point[0]) &&
    guards.is.number(point[1])
  );
};

/**
 * Checks if the given points form a valid polygon with at least 3 points
 * @param points Points of the polygon
 * @returns True if the points form a valid polygon, false otherwise
 */
export const isPolygon2D = (
  points?: OptionalReadonly<Point2D[]>,
): points is Polygon2D => {
  return (
    guards.is.array(points) && points.length >= 3 && points.every(isPoint2D)
  );
};

export const isPosition = (position: unknown): position is IPosition => {
  return (
    guards.is.object(position) &&
    guards.is.number(position.x) &&
    guards.is.number(position.y)
  );
};

export const isRect = (rect: unknown): rect is IRect => {
  return (
    guards.is.object(rect) &&
    guards.is.number(rect.x) &&
    guards.is.number(rect.y) &&
    guards.is.number(rect.width) &&
    guards.is.number(rect.height)
  );
};

/**
 * Takes any point-like input and returns a Point2D
 * @example toPoint2D(1, 2) // [1, 2]
 * @example toPoint2D({ x: 1, y: 2 }) // [1, 2]
 * @example toPoint2D([1, 2]) // [1, 2]
 * @returns a point2d object will default to [0, 0] if the input is not a number, position or Point2D
 */
export function toPoint2D(point?: PointLike): Point2D;
export function toPoint2D(x: number, y: number): Point2D;
export function toPoint2D(
  positionOrX?: number | PointLike,
  y?: number,
): Point2D {
  if (guards.is.number(positionOrX)) return [positionOrX, y ?? 0];
  if (isPoint2D(positionOrX)) return positionOrX;
  if (isPosition(positionOrX)) return [positionOrX.x, positionOrX.y];
  return [0, 0];
}

/**
 * Takes any position-like input and returns a IPosition
 * @param position
 * @returns a position object will default to [0, 0] if the input is not a number, position or Point2D
 */
export function toPosition(position: PointLike): IPosition;
export function toPosition(position: undefined): IPosition;
export function toPosition(x: number, y: number): IPosition;
export function toPosition(
  positionOrX?: number | PointLike,
  y?: number,
): IPosition {
  if (guards.is.number(positionOrX)) return { x: positionOrX, y: y ?? 0 };
  if (isPosition(positionOrX)) return positionOrX;
  if (isPoint2D(positionOrX)) return { x: positionOrX[0], y: positionOrX[1] };
  return { x: 0, y: 0 };
}

const EMPTY_RECT: IRect = Object.freeze({ x: 0, y: 0, width: 0, height: 0 });

const getRectFromPoints = (p1: PointLike, ...rest: PointLike[]): IRect => {
  const allPoints = [p1, ...rest].map((p) => toPoint2D(p));
  const xValues = allPoints.map(([x]) => x);
  const yValues = allPoints.map(([, y]) => y);
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  // Calculate center of bounding box
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Takes any point-like input and returns a IRect
 * @param p1 A rect or the first point to use to create the rectangle
 * @param additionalPoints If multiple points are provided, the rectangle will be the smallest rectangle that contains all the points
 */
export function toRect(...points: PointLike[]): IRect;
export function toRect(p1?: IRect): IRect;
export function toRect(
  rectOrP1?: IRect | PointLike,
  ...points: PointLike[]
): IRect {
  if (guards.is.undefined(rectOrP1)) {
    return { ...EMPTY_RECT };
  }
  if (isRect(rectOrP1)) {
    return rectOrP1;
  }
  return getRectFromPoints(rectOrP1, ...points);
}

/**
 * Calculates the center point of a rectangle or bounding box of points
 * @param rectOrP1 A rectangle, or the first point to calculate the bounding box center
 * @param points Additional points to calculate the bounding box center (if first param is a point)
 * @returns The center point as Point2D [x, y]
 * @example getCenter({ x: 0, y: 0, width: 10, height: 10 }) // [5, 5]
 * @example getCenter([0, 0], [10, 10]) // [5, 5]
 * @example getCenter([0, 0], [10, 0], [10, 10], [0, 10]) // [5, 5]
 */
export function getCenter(...points: PointLike[]): Point2D;
export function getCenter(rect?: IRect): Point2D;
export function getCenter(
  rectOrP1: IRect | PointLike = toRect(),
  ...points: PointLike[]
): Point2D {
  // Use implementation signature directly to handle all cases including undefined
  const rect = isRect(rectOrP1) ? rectOrP1 : toRect(rectOrP1, ...points);
  return [rect.x + rect.width / 2, rect.y + rect.height / 2];
}

/**
 * Keeps a point within the bounds of a rectangle.
 * @param p
 * @param bounds
 * @returns
 */
export const clampPoint = (p: PointLike, bounds?: IRect): Point2D => {
  const [x, y] = toPoint2D(p);
  if (!bounds) return [x, y];
  return [
    clamp(x, bounds.x, bounds.x + bounds.width),
    clamp(y, bounds.y, bounds.y + bounds.height),
  ];
};

/**
 * Returns the nearest point to the given point.
 * @param points
 * @param point
 * @param threshold If provided we ignore points that are further away than the threshold
 * @returns
 */
export const getNearestPoint = (
  points: ReadonlyArray<Point2D>,
  point: Point2D,
  threshold?: number,
): Point2D | undefined => {
  return points.reduce(
    (nearest, current) => {
      const nearestDistance = nearest
        ? getDistance(nearest, point)
        : Number.POSITIVE_INFINITY;
      const currentDistance = getDistance(current, point);

      // If the distance of the current point is greater than the threshold, keep the nearest point
      if (guards.is.number(threshold) && currentDistance > threshold)
        return nearest;

      // If the current point is not defined, return the nearest point (since it has past the threshold)
      if (!current) return nearest;

      // Return the nearest point
      return nearestDistance < currentDistance ? nearest : current;
    },
    undefined as Point2D | undefined,
  );
};

// Numerical tolerance used for geometric comparisons
const DEFAULT_EPSILON = 1e-3;

// Checks if point p lies on the line segment a-b
const isPointOnSegment = (
  p: Point2D,
  a: Point2D,
  b: Point2D,
  epsilon: number = DEFAULT_EPSILON,
): boolean => {
  const [px, py] = p;
  const [ax, ay] = a;
  const [bx, by] = b;

  // Area of the parallelogram (cross product) must be ~0 for colinearity
  const cross = (bx - ax) * (py - ay) - (by - ay) * (px - ax);
  if (Math.abs(cross) > epsilon) return false;

  // Bounding-box check (with tolerance) to ensure the point is within the segment extents
  const minX = Math.min(ax, bx) - epsilon;
  const maxX = Math.max(ax, bx) + epsilon;
  const minY = Math.min(ay, by) - epsilon;
  const maxY = Math.max(ay, by) + epsilon;
  if (px < minX || px > maxX || py < minY || py > maxY) return false;
  return true;
};

/**
 * Checks if a point is within a polygon
 * @param point The point to check
 * @param polygon The polygon to check
 * @param options The options for the check. If inclusive is true, the point is considered within the polygon if it is on the boundary.
 * @returns True if the point is within the polygon, false otherwise
 */
export const isPointInPolygon = (
  point: Point2D,
  polygon: OptionalReadonly<Point2D[]>,
  options: { inclusive?: boolean; epsilon?: number } = {},
): boolean => {
  const inclusive = options.inclusive ?? true;
  if (!isPolygon2D(polygon)) return false;

  // Explicit boundary detection. If on boundary, return according to inclusivity setting.
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = required(polygon[j], INVALID_POLYGON_INPUT);
    const b = required(polygon[i], INVALID_POLYGON_INPUT);
    if (isPointOnSegment(point, a, b, options.epsilon)) return inclusive;
  }

  // Ray casting to the right
  let inside = false;
  const [px, py] = point;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = required(polygon[i], INVALID_POLYGON_INPUT);
    const [xj, yj] = required(polygon[j], INVALID_POLYGON_INPUT);

    const intersects =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

const orientation = (
  a: Point2D,
  b: Point2D,
  c: Point2D,
  epsilon: number = DEFAULT_EPSILON,
): number => {
  const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]);
  if (Math.abs(value) <= epsilon) return 0;
  return value > 0 ? 1 : 2;
};

export const segmentsIntersect = (
  p1: Point2D,
  p2: Point2D,
  q1: Point2D,
  q2: Point2D,
  epsilon: number = DEFAULT_EPSILON,
): boolean => {
  const o1 = orientation(p1, p2, q1, epsilon);
  const o2 = orientation(p1, p2, q2, epsilon);
  const o3 = orientation(q1, q2, p1, epsilon);
  const o4 = orientation(q1, q2, p2, epsilon);

  if (o1 !== o2 && o3 !== o4) return true;

  if (o1 === 0 && isPointOnSegment(q1, p1, p2, epsilon)) return true;
  if (o2 === 0 && isPointOnSegment(q2, p1, p2, epsilon)) return true;
  if (o3 === 0 && isPointOnSegment(p1, q1, q2, epsilon)) return true;
  if (o4 === 0 && isPointOnSegment(p2, q1, q2, epsilon)) return true;

  return false;
};

/**
 * True when segments cross in an X, excluding collinear overlap and T-junctions.
 */
const segmentsCrossProperly = (
  p1: Point2D,
  p2: Point2D,
  q1: Point2D,
  q2: Point2D,
  epsilon: number = DEFAULT_EPSILON,
): boolean => {
  const o1 = orientation(p1, p2, q1, epsilon);
  const o2 = orientation(p1, p2, q2, epsilon);
  const o3 = orientation(q1, q2, p1, epsilon);
  const o4 = orientation(q1, q2, p2, epsilon);
  return o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4;
};

/**
 * Compute the intersection point of two line segments, if any.
 * Returns the intersection as a {@link Point2D} or undefined when
 * the segments are parallel or do not intersect.
 * @param p1 Start of first segment
 * @param p2 End of first segment
 * @param q1 Start of second segment
 * @param q2 End of second segment
 */
export const getSegmentIntersectionPoint = (
  p1: Point2D,
  p2: Point2D,
  q1: Point2D,
  q2: Point2D,
): Point2D | undefined => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const [x3, y3] = q1;
  const [x4, y4] = q2;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-12) return undefined;

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  // Both parameters must be in [0, 1] for the intersection to lie on both segments.
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return undefined;

  return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
};

/**
 * Check whether two polygons overlap or touch.
 * @param polygonA First polygon
 * @param polygonB Second polygon
 * @returns True when polygons intersect
 */
export const doPolygonsIntersect = (
  polygonA: OptionalReadonly<Point2D[]>,
  polygonB: OptionalReadonly<Point2D[]>,
): boolean => {
  if (!isPolygon2D(polygonA) || !isPolygon2D(polygonB)) return false;

  // Point inclusion test
  if (
    polygonA.some((point) => isPointInPolygon(point, polygonB)) ||
    polygonB.some((point) => isPointInPolygon(point, polygonA))
  ) {
    return true;
  }

  // Edge intersection test
  for (let i = 0; i < polygonA.length; i += 1) {
    const aStart = required(polygonA[i], INVALID_POLYGON_INPUT);
    const aEnd = required(
      polygonA[(i + 1) % polygonA.length],
      INVALID_POLYGON_INPUT,
    );
    for (let j = 0; j < polygonB.length; j += 1) {
      const bStart = required(polygonB[j], INVALID_POLYGON_INPUT);
      const bEnd = required(
        polygonB[(j + 1) % polygonB.length],
        INVALID_POLYGON_INPUT,
      );
      if (segmentsIntersect(aStart, aEnd, bStart, bEnd)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * True when {@link point} lies on the boundary of {@link polygon}.
 */
const isPointOnPolygonBoundary = (
  point: Point2D,
  polygon: OptionalReadonly<Point2D[]>,
): boolean =>
  isPointInPolygon(point, polygon) &&
  !isPointInPolygon(point, polygon, { inclusive: false });

/**
 * True when a vertex or edge midpoint of {@link polygon} is strictly inside {@link other}.
 * Axis-aligned overlaps often have vertices on the shared edges, so midpoints are required.
 */
const hasStrictInteriorSample = (
  polygon: OptionalReadonly<Point2D[]>,
  other: OptionalReadonly<Point2D[]>,
): boolean => {
  if (
    polygon.some((point) =>
      isPointInPolygon(point, other, { inclusive: false }),
    )
  ) {
    return true;
  }
  for (let i = 0; i < polygon.length; i += 1) {
    const start = required(polygon[i], INVALID_POLYGON_INPUT);
    const end = required(
      polygon[(i + 1) % polygon.length],
      INVALID_POLYGON_INPUT,
    );
    const mid: Point2D = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
    if (isPointInPolygon(mid, other, { inclusive: false })) return true;
  }
  return false;
};

/**
 * True when two polygons share interior area.
 * Shared edges or vertices (party-wall adjacency) are not interior overlap.
 * {@link doPolygonsIntersect} is true for those touches; this is not.
 */
export const doPolygonsOverlapInterior = (
  polygonA: OptionalReadonly<Point2D[]>,
  polygonB: OptionalReadonly<Point2D[]>,
): boolean => {
  if (!isPolygon2D(polygonA) || !isPolygon2D(polygonB)) return false;

  if (
    hasStrictInteriorSample(polygonA, polygonB) ||
    hasStrictInteriorSample(polygonB, polygonA)
  ) {
    return true;
  }

  for (let i = 0; i < polygonA.length; i += 1) {
    const aStart = required(polygonA[i], INVALID_POLYGON_INPUT);
    const aEnd = required(
      polygonA[(i + 1) % polygonA.length],
      INVALID_POLYGON_INPUT,
    );
    for (let j = 0; j < polygonB.length; j += 1) {
      const bStart = required(polygonB[j], INVALID_POLYGON_INPUT);
      const bEnd = required(
        polygonB[(j + 1) % polygonB.length],
        INVALID_POLYGON_INPUT,
      );
      if (segmentsCrossProperly(aStart, aEnd, bStart, bEnd)) return true;
    }
  }

  // Identical (or boundary-coincident) footprints share area with no crossing.
  return (
    polygonA.every((point) => isPointOnPolygonBoundary(point, polygonB)) &&
    polygonB.every((point) => isPointOnPolygonBoundary(point, polygonA))
  );
};

/**
 * Length of the collinear overlap between two segments.
 * Returns 0 when the segments are not collinear, only touch at a point,
 * or are parallel but offset.
 */
export const getCollinearSegmentOverlapLength = (
  a1: Point2D,
  a2: Point2D,
  b1: Point2D,
  b2: Point2D,
  epsilon: number = DEFAULT_EPSILON,
): number => {
  const ax = a2[0] - a1[0];
  const ay = a2[1] - a1[1];
  const aLen = Math.hypot(ax, ay);
  if (aLen <= epsilon) return 0;

  const bx = b2[0] - b1[0];
  const by = b2[1] - b1[1];
  if (Math.hypot(bx, by) <= epsilon) return 0;

  // Perpendicular distance of each B endpoint to line A must be within epsilon.
  const perpDist = (p: Point2D) =>
    Math.abs(ax * (p[1] - a1[1]) - ay * (p[0] - a1[0])) / aLen;
  if (perpDist(b1) > epsilon || perpDist(b2) > epsilon) return 0;

  // Project onto A and take the overlapping 1D interval.
  const projectT = (p: Point2D) =>
    ((p[0] - a1[0]) * ax + (p[1] - a1[1]) * ay) / (aLen * aLen);
  const tMin = Math.max(0, Math.min(projectT(b1), projectT(b2)));
  const tMax = Math.min(1, Math.max(projectT(b1), projectT(b2)));
  const overlapLen = (tMax - tMin) * aLen;
  return overlapLen > epsilon ? overlapLen : 0;
};

/**
 * Total length of collinear overlapping edges between two polygons.
 * Party-wall contact contributes; a shared vertex does not.
 */
export const getSharedBoundaryLength = (
  polygonA: OptionalReadonly<Point2D[]>,
  polygonB: OptionalReadonly<Point2D[]>,
): number => {
  if (!isPolygon2D(polygonA) || !isPolygon2D(polygonB)) return 0;

  let length = 0;
  for (let i = 0; i < polygonA.length; i += 1) {
    const aStart = required(polygonA[i], INVALID_POLYGON_INPUT);
    const aEnd = required(
      polygonA[(i + 1) % polygonA.length],
      INVALID_POLYGON_INPUT,
    );
    for (let j = 0; j < polygonB.length; j += 1) {
      const bStart = required(polygonB[j], INVALID_POLYGON_INPUT);
      const bEnd = required(
        polygonB[(j + 1) % polygonB.length],
        INVALID_POLYGON_INPUT,
      );
      length += getCollinearSegmentOverlapLength(aStart, aEnd, bStart, bEnd);
    }
  }
  return length;
};

/**
 * True when every vertex and edge midpoint of {@link inner} lies in {@link outer}.
 * Boundary points count as inside so identical footprints are contained.
 */
export const isPolygonContainedInPolygon = (
  inner: OptionalReadonly<Point2D[]>,
  outer: OptionalReadonly<Point2D[]>,
): boolean => {
  if (!isPolygon2D(inner) || !isPolygon2D(outer)) return false;
  if (
    inner.some((point) => !isPointInPolygon(point, outer, { inclusive: true }))
  ) {
    return false;
  }
  for (let i = 0; i < inner.length; i += 1) {
    const start = required(inner[i], INVALID_POLYGON_INPUT);
    const end = required(inner[(i + 1) % inner.length], INVALID_POLYGON_INPUT);
    const mid: Point2D = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
    if (!isPointInPolygon(mid, outer, { inclusive: true })) return false;
  }
  return true;
};

const isAxisAlignedRectangle = (
  polygon: OptionalReadonly<Point2D[]>,
): polygon is Polygon2D => {
  if (!isPolygon2D(polygon) || polygon.length !== 4) return false;
  for (let i = 0; i < polygon.length; i += 1) {
    const start = required(polygon[i], INVALID_POLYGON_INPUT);
    const end = required(
      polygon[(i + 1) % polygon.length],
      INVALID_POLYGON_INPUT,
    );
    const dx = Math.abs(end[0] - start[0]);
    const dy = Math.abs(end[1] - start[1]);
    const isOrtho =
      (dx <= DEFAULT_EPSILON && dy > DEFAULT_EPSILON) ||
      (dy <= DEFAULT_EPSILON && dx > DEFAULT_EPSILON);
    if (!isOrtho) return false;
  }
  return true;
};

/**
 * True when {@link MartinezGeometry} is a single Polygon (not MultiPolygon).
 * Both nest arrays; the distinction is depth to the first coordinate.
 */
export const isMartinezPolygon = (
  g: MartinezGeometry,
): g is MartinezPolygon => {
  if (!g.length) return false;
  const firstRing = g[0];
  if (!firstRing?.length) return false;
  const firstOfRing = firstRing[0];
  if (!Array.isArray(firstOfRing)) return false;
  return typeof firstOfRing[0] === 'number';
};

/**
 * Convert a {@link Polygon2D} to a closed Martinez ring (last point equals first).
 */
const polygon2DToMartinezRing = (polygon: Polygon2D): MartinezRing => {
  const ring: MartinezRing = polygon.map(([x, y]) => [x, y]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([first[0], first[1]]);
  }
  return ring;
};

/**
 * Strip a closing duplicate vertex so {@link computePolygonArea} can run.
 */
const stripClosedRing = (ring: MartinezRing): Point2D[] => {
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && first[0] === last[0] && first[1] === last[1]) {
    return ring.slice(0, -1);
  }
  return ring;
};

/**
 * Area of a Martinez intersection, subtracting holes from each polygon.
 */
const areaOfMartinezGeometry = (g: MartinezGeometry): number => {
  const polygons = isMartinezPolygon(g) ? [g] : g;
  let area = 0;
  for (const polygon of polygons) {
    const outer = polygon[0];
    if (!outer) continue;
    const outerPoly = stripClosedRing(outer);
    if (!isPolygon2D(outerPoly)) continue;
    area += computePolygonArea(outerPoly);
    for (let i = 1; i < polygon.length; i += 1) {
      const hole = polygon[i];
      if (!hole) continue;
      const holePoly = stripClosedRing(hole);
      if (!isPolygon2D(holePoly)) continue;
      area -= computePolygonArea(holePoly);
    }
  }
  return Math.max(0, area);
};

/**
 * Distance below which a vertex counts as float noise on another ring.
 * 1e-8 m is far below the 1 mm geometry epsilon. Moving the vertex can still
 * change covered GFA by square metres when Martinez returns a different polygon.
 */
const NUMERIC_ON_EDGE_EPSILON = 1e-8;

/**
 * Shift {@link point} off {@link other} when float noise puts it on an edge interior.
 * That degeneracy froze a project open. It is not every way Martinez can fail to return.
 * Exact shared edges (`distance === 0`) stay put, and so does a graze farther than
 * {@link NUMERIC_ON_EDGE_EPSILON}. Either can still block the main thread.
 * Shared endpoints stay put; only an interior hit is moved.
 * @param point Vertex to test
 * @param other Ring the vertex may lie on
 */
const liftPointOffForeignEdges = (
  point: Point2D,
  other: Polygon2D,
): Point2D => {
  for (let index = 0; index < other.length; index += 1) {
    const start = required(other[index], INVALID_POLYGON_INPUT);
    const end = required(
      other[(index + 1) % other.length],
      INVALID_POLYGON_INPUT,
    );
    const edgeX = end[0] - start[0];
    const edgeY = end[1] - start[1];
    const edgeLength = Math.hypot(edgeX, edgeY);
    if (edgeLength <= NUMERIC_ON_EDGE_EPSILON) continue;

    const cross = edgeX * (point[1] - start[1]) - edgeY * (point[0] - start[0]);
    const distance = Math.abs(cross) / edgeLength;
    // Exact contact stays. A graze beyond this epsilon can still loop.
    if (distance === 0 || distance > NUMERIC_ON_EDGE_EPSILON) continue;

    const along =
      (edgeX * (point[0] - start[0]) + edgeY * (point[1] - start[1])) /
      (edgeLength * edgeLength);
    if (
      along <= NUMERIC_ON_EDGE_EPSILON ||
      along >= 1 - NUMERIC_ON_EDGE_EPSILON
    ) {
      continue;
    }

    // Left unit normal. 1e-8 m clears the line; the clip area can still change.
    const normalX = -edgeY / edgeLength;
    const normalY = edgeX / edgeLength;
    const lifted: Point2D = [
      point[0] + normalX * NUMERIC_ON_EDGE_EPSILON,
      point[1] + normalY * NUMERIC_ON_EDGE_EPSILON,
    ];
    return lifted;
  }
  return point;
};

/**
 * Copy {@link polygon} with vertices lifted off {@link other}.
 * @param polygon Ring passed to Martinez
 * @param other The other ring
 */
const liftVerticesOffForeignEdges = (
  polygon: Polygon2D,
  other: Polygon2D,
): Polygon2D => {
  const lifted: Point2D[] = polygon.map((point) =>
    liftPointOffForeignEdges(point, other),
  );
  if (!isPolygon2D(lifted)) return polygon;
  return lifted;
};

/**
 * Intersection area via Martinez. Returns 0 when clipping throws or is empty.
 * Martinez can hang on some shared-edge diffs; containment/AABB paths avoid it.
 * Vertices that sit on the other ring within float noise are lifted off first.
 * Exact shared edges and wider grazes are not lifted and can still hang.
 */
let martinezIntersectionCount = 0;

/** How many times {@link intersectPolygonArea} has called Martinez. For tests. */
export const getMartinezIntersectionCount = (): number =>
  martinezIntersectionCount;

/** Reset {@link getMartinezIntersectionCount}. For tests. */
export const resetMartinezIntersectionCount = (): void => {
  martinezIntersectionCount = 0;
};

const intersectPolygonArea = (
  polygonA: Polygon2D,
  polygonB: Polygon2D,
): number => {
  try {
    martinezIntersectionCount += 1;
    const subject = liftVerticesOffForeignEdges(polygonA, polygonB);
    const clipping = liftVerticesOffForeignEdges(polygonB, polygonA);
    const result = safeMartinezIntersection(
      [polygon2DToMartinezRing(subject)],
      [polygon2DToMartinezRing(clipping)],
    );
    if (!result) return 0;
    return areaOfMartinezGeometry(result);
  } catch {
    return 0;
  }
};

/**
 * Interior overlap area of two polygons.
 * Nested or identical footprints use the contained area. Axis-aligned
 * rectangles use an AABB intersection. Other partial overlaps use Martinez.
 */
export const getPolygonOverlapArea = (
  polygonA: OptionalReadonly<Point2D[]>,
  polygonB: OptionalReadonly<Point2D[]>,
): number => {
  if (!doPolygonsOverlapInterior(polygonA, polygonB)) return 0;
  if (isPolygonContainedInPolygon(polygonA, polygonB)) {
    return computePolygonArea(polygonA);
  }
  if (isPolygonContainedInPolygon(polygonB, polygonA)) {
    return computePolygonArea(polygonB);
  }
  if (isAxisAlignedRectangle(polygonA) && isAxisAlignedRectangle(polygonB)) {
    const overlap = getIntersection(toRect(...polygonA), toRect(...polygonB));
    return Math.max(0, overlap.width * overlap.height);
  }
  if (!isPolygon2D(polygonA) || !isPolygon2D(polygonB)) return 0;
  return intersectPolygonArea(polygonA, polygonB);
};

/**
 * Multiplies a point by a scalar or a point
 * @param point The point to multiply
 * @param scalar The scalar or point to multiply the point by
 * @returns The multiplied point
 */
export const multiplyPoint2D = (
  point: Point2D,
  scalar: number | PointLike = 1,
): Point2D => {
  const [scalarX, scalarY] = guards.is.number(scalar)
    ? [scalar, scalar]
    : toPoint2D(scalar);
  return [point[0] * scalarX, point[1] * scalarY];
};

/**
 * Checks if two points are equal within a given precision
 * @param a The first point
 * @param b The second point
 * @param precision The precision to use for the comparison (default 0.0001 meters)
 * @returns True if the points are equal, false otherwise
 */
export const isEqualPoints = (
  a: Point2D,
  b: Point2D,
  precision: number = 0.0001,
): boolean =>
  isCloseTo(a[0], b[0], precision) && isCloseTo(a[1], b[1], precision);

/**
 * Checks if two points are equal (exact equality, suitable for screen coordinates)
 * Handles optional points - returns true if both are undefined, false if only one is undefined
 * @param a The first point (optional)
 * @param b The second point (optional)
 * @returns True if the points are equal or both undefined, false otherwise
 */
export const arePointsEqual = (a?: Point2D, b?: Point2D): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a[0] === b[0] && a[1] === b[1];
};

/**
 * Checks if a point is in an array of points
 * @param points The array of points
 * @param point The point to check
 * @returns True if the point is in the array, false otherwise
 */
export const hasPoint = (points: Point2D[], point: Point2D): boolean =>
  points.some((p) => isEqualPoints(p, point));

/**
 * Gets a point on a line at a given ratio (0-1). If outside the range you will get the projected point.
 * @param line The line to get the point on
 * @param t The ratio of the line to get the point on
 * @returns The point on the line
 */
export const getPointOnLine = (line: Line2D, t: number): Point2D => {
  const [p1, p2] = line;
  const x1 = p1[0];
  const y1 = p1[1];
  const x2 = p2[0];
  const y2 = p2[1];

  const x = x1 + t * (x2 - x1);
  const y = y1 + t * (y2 - y1);

  return [x, y];
};

/**
 * Project a point onto a line segment, clamped to the segment bounds.
 * Use this so drag offset is computed from a point on the segment, not raw mouse.
 *
 * @param point Point to project (e.g. pointer position)
 * @param segmentStart Segment start
 * @param segmentEnd Segment end
 * @returns Closest point on the segment to {@link point}
 */
export const projectPointOntoSegmentClamped = (
  point: Point2D,
  segmentStart: Point2D,
  segmentEnd: Point2D,
): Point2D => {
  const [px, py] = point;
  const [ax, ay] = segmentStart;
  const [bx, by] = segmentEnd;

  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;

  const abLengthSquared = abx * abx + aby * aby;
  if (abLengthSquared === 0) return segmentStart;

  const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLengthSquared));

  return [ax + t * abx, ay + t * aby];
};

/**
 * Moves a list of points by a delta
 * @param points The points to move
 * @param delta The delta to move the points by
 * @returns
 */
export const movePoints = (points: Point2D[], delta: Point2D): Point2D[] => {
  return points.map((p) => [p[0] + delta[0], p[1] + delta[1]]);
};
